import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import {
  Button,
  ButtonLink,
  FontAwesomeIcon,
  Stack,
  TableCell,
  useModals,
} from "@skailer-com/uikit";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

interface EmployeeHoverCardProps {
  employee: ApiEmployeeShort;
  position?: ReactNode;
  children: ReactNode;
}

export const EmployeeHoverCard = ({ employee, position, children }: EmployeeHoverCardProps) => {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "top-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    delay: { open: 500, close: 100 },
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  if (employee.is_archived) return <>{children}</>;

  return (
    <>
      <span ref={refs.setReference} className={"inline-flex max-w-full"} {...getReferenceProps()}>
        {children}
      </span>
      {open && (
        <FloatingPortal>
          <div
            // floating-ui refs.setFloating — callback-сеттер, а не чтение .current
            // eslint-disable-next-line react-hooks/refs
            ref={refs.setFloating}
            className={"z-popover"}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <Stack
              gap={"md"}
              className={"w-64 rounded-xs border border-dark/10 bg-light p-4 shadow-md"}
            >
              <Stack gap={"sm"}>
                <EmployeeAvatar employee={employee} size={"md"} />
                <TableCell caption={position}>{renderEmployeeName(employee)}</TableCell>
              </Stack>
              <Stack direction={"horizontal"} gap={"sm"}>
                <Button
                  size={"sm"}
                  onClick={() => openModal(EmployeeViewModal, { employeeId: employee.id })}
                >
                  {t("core:preview")}
                </Button>
                <ButtonLink
                  size={"sm"}
                  variant={"plain"}
                  href={`/employees/${employee.id}`}
                  blank
                  rightIcon={<FontAwesomeIcon icon={"fa-light fa-arrow-up-right-from-square"} />}
                >
                  {t("core:view_profile")}
                </ButtonLink>
              </Stack>
            </Stack>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
